import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Bug, Lightbulb, AlertCircle, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function FeedbackPage() {
  const navigate = useNavigate();
  
  // Bug report state
  const [showBugDialog, setShowBugDialog] = useState(false);
  const [bugCategory, setBugCategory] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugSubmitting, setBugSubmitting] = useState(false);

  // Feature request state
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');
  const [featureSubmitting, setFeatureSubmitting] = useState(false);

  const handleBugSubmit = async () => {
    if (!bugCategory) {
      toast.error('Please select a bug category');
      return;
    }
    if (!bugDescription.trim()) {
      toast.error('Please describe the issue');
      return;
    }

    setBugSubmitting(true);
    try {
      const { data } = await api.post('/api/support/bug-report', {
        category: bugCategory,
        description: bugDescription,
      });
      
      toast.success(data.message || `Bug report ${data.ticket_id} submitted successfully!`);
      setBugCategory('');
      setBugDescription('');
      setShowBugDialog(false);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit bug report. Please try again.');
      }
    } finally {
      setBugSubmitting(false);
    }
  };

  const handleFeatureSubmit = async () => {
    if (!featureTitle.trim()) {
      toast.error('Please enter a feature title');
      return;
    }
    if (!featureDescription.trim()) {
      toast.error('Please describe the feature');
      return;
    }

    setFeatureSubmitting(true);
    try {
      const { data } = await api.post('/api/support/feature-request', {
        title: featureTitle,
        description: featureDescription,
      });
      
      toast.success(data.message || `Feature request ${data.ticket_id} submitted successfully!`);
      setFeatureTitle('');
      setFeatureDescription('');
      setShowFeatureDialog(false);
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit feature request. Please try again.');
      }
    } finally {
      setFeatureSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6" data-testid="feedback-page">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl sm:text-4xl font-bold font-['Outfit'] flex items-center justify-center gap-2">
            <MessageSquare className="h-8 w-8 text-primary" />
            Feedback
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Help us improve TheMoneyMap. Report bugs or suggest new features - we read every submission!
          </p>
        </div>

        {/* Main Cards */}
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Bug Report */}
          <Card className="border-red-200 dark:border-red-900 hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2 text-red-600 dark:text-red-400">
                <Bug className="h-6 w-6" />
                Report a Bug
              </CardTitle>
              <CardDescription>
                Found something broken? Let us know and we&apos;ll fix it fast.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Dialog open={showBugDialog} onOpenChange={setShowBugDialog}>
                <DialogTrigger asChild>
                  <Button 
                    size="lg" 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                  >
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Report Bug
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                      <Bug className="h-5 w-5 text-red-600" />
                      Report a Bug
                    </DialogTitle>
                    <DialogDescription>
                      Help us improve by reporting issues. We&apos;ll send your report to our team.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="bug-category">Issue Category *</Label>
                      <Select value={bugCategory} onValueChange={setBugCategory}>
                        <SelectTrigger id="bug-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UI Inconsistency">UI Inconsistency</SelectItem>
                          <SelectItem value="Performance Issues">Performance Issues</SelectItem>
                          <SelectItem value="Data Missing/Not Showing">Data Missing/Not Showing</SelectItem>
                          <SelectItem value="Login/Authentication Issues">Login/Authentication Issues</SelectItem>
                          <SelectItem value="Statement Upload Issues">Statement Upload Issues</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bug-description">Description *</Label>
                      <Textarea
                        id="bug-description"
                        placeholder="Please describe the issue in detail. What were you doing? What happened? What did you expect to happen?"
                        value={bugDescription}
                        onChange={(e) => setBugDescription(e.target.value)}
                        rows={6}
                        className="resize-none"
                        maxLength={500}
                      />
                      <p className="text-xs text-muted-foreground">
                        {bugDescription.length}/500 characters
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowBugDialog(false)} disabled={bugSubmitting}>
                        Cancel
                      </Button>
                      <Button onClick={handleBugSubmit} disabled={bugSubmitting}>
                        {bugSubmitting ? (
                          <>
                            <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Submit Report
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>

          {/* Feature Request */}
          <Card className="border-blue-200 dark:border-blue-900 hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-xl font-['Outfit'] flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <Lightbulb className="h-6 w-6" />
                Feature Request
              </CardTitle>
              <CardDescription>
                Have an idea? Share your suggestions for new features.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Dialog open={showFeatureDialog} onOpenChange={setShowFeatureDialog}>
                <DialogTrigger asChild>
                  <Button 
                    size="lg" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Lightbulb className="h-5 w-5 mr-2" />
                    Request Feature
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-blue-600" />
                      Request a Feature
                    </DialogTitle>
                    <DialogDescription>
                      Tell us what feature you&apos;d like to see. We love hearing your ideas!
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="feature-title">Feature Title *</Label>
                      <Input
                        id="feature-title"
                        placeholder="e.g., Export transactions to CSV"
                        value={featureTitle}
                        onChange={(e) => setFeatureTitle(e.target.value)}
                        maxLength={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="feature-description">Description *</Label>
                      <Textarea
                        id="feature-description"
                        placeholder="Describe the feature you'd like to see. What problem does it solve? How should it work?"
                        value={featureDescription}
                        onChange={(e) => setFeatureDescription(e.target.value)}
                        rows={6}
                        className="resize-none"
                        maxLength={500}
                      />
                      <p className="text-xs text-muted-foreground">
                        {featureDescription.length}/500 characters
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setShowFeatureDialog(false)} disabled={featureSubmitting}>
                        Cancel
                      </Button>
                      <Button onClick={handleFeatureSubmit} disabled={featureSubmitting}>
                        {featureSubmitting ? (
                          <>
                            <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Submit Request
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="bg-muted/30">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground text-center">
              Rate limit: 3 reports per day per category • Reports sent to <a href="mailto:info@themoneymap.online" className="underline font-medium">info@themoneymap.online</a>
            </p>
          </CardContent>
        </Card>

        {/* Back Button */}
        <div className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground"
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
